import { useState, useRef } from 'react';
import { Box, Typography, IconButton, Paper, Skeleton } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ClientCardsRowProps, getCardBorder } from '../types';
import { ClientCard } from './ClientCard';

/**
 * ClientCardsRow - Horizontal scrollable row of client cards.
 *
 * Features:
 * - Shows all clients with their health statistics
 * - Horizontal scrolling when more than 4 clients
 * - Scroll arrows for navigation
 * - Click a card to filter devices by that client
 */
export function ClientCardsRow({
  clients,
  selectedClientId,
  onClientSelect,
  loading = false,
  sx,
}: ClientCardsRowProps) {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 336; // card width (320) + gap (16)
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleClientClick = (clientId: number) => {
    // Toggle selection: if already selected, deselect (show all)
    onClientSelect(selectedClientId === clientId ? 'all' : clientId);
  };

  if (loading) {
    return (
      <Box sx={{ mb: 3, mt: 1, ...sx }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Skeleton animation="wave" variant="text" width={180} height={28} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {[0, 1, 2, 3].map((index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                flex: 1,
                borderRadius: 2,
                border: getCardBorder(theme),
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Box>
                  <Skeleton animation="wave" variant="text" width={120} height={24} />
                  <Skeleton animation="wave" variant="text" width={80} height={16} />
                </Box>
              </Box>
              {/* Health bar */}
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Skeleton animation="wave" variant="text" width={40} />
                  <Skeleton animation="wave" variant="text" width={30} />
                </Box>
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={6}
                  sx={{ borderRadius: 3 }}
                />
              </Box>
              {/* Status dots */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton animation="wave" variant="circular" width={20} height={20} />
                <Skeleton animation="wave" variant="circular" width={20} height={20} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  const showScrollArrows = clients.length > 4;

  return (
    <Box sx={{ mb: 3, mt: 1, ...sx }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Click to Filter by Client
        </Typography>
        {/* Scroll Arrows */}
        {showScrollArrows && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll clients left"
              sx={{
                bgcolor: canScrollLeft ? 'action.hover' : 'transparent',
                '&:disabled': { opacity: 0.3 },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll clients right"
              sx={{
                bgcolor: canScrollRight ? 'action.hover' : 'transparent',
                '&:disabled': { opacity: 0.3 },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Scrollable Container */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: showScrollArrows ? 'auto' : 'visible',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
          pb: 1, // Padding for box-shadow visibility
          px: 0.5, // Side padding for card shadows
        }}
      >
        {clients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            isSelected={selectedClientId === client.id}
            onClick={() => handleClientClick(client.id)}
            sx={{
              flex: showScrollArrows ? '0 0 auto' : '1 1 0',
              width: showScrollArrows ? 320 : 'auto',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default ClientCardsRow;
